// 482572 — Web Locks API (`navigator.locks`): exclusão mútua ENTRE ABAS da mesma origem, nativa do
// navegador (disponível em todos há anos — não é experimental). Usada aqui para dois fins:
//
//   1. Impedir que DUAS ABAS reivindiquem o MESMO snapshot órfão ao mesmo tempo
//      (executarComTravaExclusiva) — o guard de módulo de useRecuperarOrcamentoEmAndamento.ts é
//      por aba e não protege disso.
//   2. Saber se uma aba ainda está VIVA sem depender de timeout (abaTemTrava): cada aba segura um
//      lock com o próprio abaId pela vida inteira, e o navegador o libera SOZINHO quando a aba
//      morre/trava/é fechada — que é exatamente o evento que abaEstaViva.ts tenta inferir hoje com
//      um ping de 400ms no BroadcastChannel.
//
// TIPAGEM: o `lib.dom.d.ts` do TypeScript 4.6.3 (versão resolvida neste projeto, ver
// Aprendizado_JavaScript_Build.md #342) já traz `Lock`, `LockManager`, `LockOptions`, `LockInfo` e
// `LockMode`, mas NÃO traz o mixin `NavigatorLocks` — `Navigator` não expõe `locks`, e o acesso dá
// `TS2339`. A augmentation abaixo só religa a propriedade ao tipo que já existe (não é `any`).
declare global {
    interface Navigator {
        readonly locks: LockManager
    }
}

const PREFIXO_TRAVA_ABA = 'mu_aba_'

export function nomeTravaDaAba(abaId: string): string {
    return PREFIXO_TRAVA_ABA + abaId
}

export function suportaTravaEntreAbas(): boolean {
    return typeof navigator !== 'undefined' && navigator.locks != null && typeof navigator.locks.request === 'function'
}

// Adquire o lock desta aba e o segura pela vida inteira dela: o callback devolve uma Promise que
// NUNCA resolve de propósito — enquanto ela estiver pendente, o lock continua "held". Não dá para
// await neste retorno (ele também nunca resolve); é fire-and-forget por natureza.
// O navegador libera o lock automaticamente quando a aba é fechada, recarregada ou morre — sem
// nenhum código de limpeza nosso, e sem o falso-negativo de "aba viva mas lenta não respondeu".
let travaDaAbaAtualSolicitada = false
export function manterTravaDaAbaAtual(abaId: string | null | undefined): void {
    if (!abaId || travaDaAbaAtualSolicitada || !suportaTravaEntreAbas()) return
    travaDaAbaAtualSolicitada = true

    navigator.locks
        .request(nomeTravaDaAba(abaId), { mode: 'exclusive' }, () => new Promise<void>(() => { }))
        .catch(() => { travaDaAbaAtualSolicitada = false })
}

// Conjunto de abaIds que HOJE seguram o próprio lock — uma única consulta cobre todas as abas, ao
// contrário do ping por aba (que custa até TIMEOUT_PROBE_ABA_VIVA_MS cada).
export async function abasComTravaAtiva(): Promise<Set<string>> {
    if (!suportaTravaEntreAbas()) return new Set()

    try {
        const estado = await navigator.locks.query()
        const nomes = (estado.held ?? [])
            .map(lock => lock.name ?? '')
            .filter(nome => nome.indexOf(PREFIXO_TRAVA_ABA) === 0)
            .map(nome => nome.slice(PREFIXO_TRAVA_ABA.length))
        return new Set(nomes)
    } catch {
        return new Set()
    }
}

export async function abaTemTrava(abaId: string): Promise<boolean> {
    const abas = await abasComTravaAtiva()
    return abas.has(abaId)
}

// Executa `acao` com exclusividade entre abas. `ifAvailable: true` = NÃO entra em fila: se outra
// aba já está executando algo com este mesmo nome, desiste na hora e devolve `false`.
//
// ATENÇÃO (pegadinha da API): com `ifAvailable`, o callback é chamado MESMO quando o lock não foi
// concedido — aí `lock` vem `null`. Sem o guard abaixo, o código "protegido" rodaria em paralelo,
// exatamente o que se queria evitar.
//
// Sem suporte a Web Locks, executa sem proteção (comportamento idêntico ao de antes desta função) —
// degradar para "não roda" seria pior: quebraria a recuperação em vez de só não protegê-la.
export async function executarComTravaExclusiva(nome: string, acao: () => Promise<void>): Promise<boolean> {
    if (!suportaTravaEntreAbas()) {
        await acao()
        return true
    }

    let executou = false
    await navigator.locks.request(nome, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
        if (!lock) return
        executou = true
        await acao()
    })
    return executou
}
